import { geoMercator, geoPath } from 'd3-geo';

import borders from './data/cnn-borders.json';
import colombia from './data/colombia.json';
import plates from './data/plate-boundaries.json';
import quakesRaw from './data/quakes.json';
import hillshadeDesktop from './images/hillshade-desktop.png';
import hillshadeMobile from './images/hillshade-mobile.png';
import {
  EXTENT, MOBILE_EXTENT, COLORS, MIN_MAGNITUDE,
} from './config';
import './styles/graphic.scss';

// Every quake is drawn the same size (magnitude is not encoded). All markers
// are grey except the breaking-news quake, which is CNN red.
const QUAKE_RADIUS = 6;
const QUAKE_GREY = '#7a7a7a';
const QUAKE_RED = '#cc0000';
const QUAKE_ORANGE = '#F18851';

// --- Quake field mapping (CNN/USGS earthquake archive) -------------------
// Source: https://ix.cnn.io/data/earthquakes-next/archive/ (see
// scripts/build_quakes.py). Each row has `lat`, `lon`, `mag`, `timeStr`,
// `place`, `id`. If the feed changes field names, adjust this single object.
const FIELDS = {
  lat: 'lat',
  lon: 'lon',
  mag: 'mag',
  time: 'timeStr',
  place: 'place',
  id: 'id',
};

// Always-on geographic labels, placed by [lon, lat]. Hand-tuned for this zoom.
const COUNTRY_LABELS = [
  { c: [-101, 22], t: 'MEXICO' },
  { c: [-85.2, 12.6], t: 'NICARAGUA', hideMobile: true },
  { c: [-65.5, 7.4], t: 'VENEZUELA' },
  { c: [-73.2, 1.2], t: 'COLOMBIA', mobileDy: 14 },
  { c: [-62, -3.5], t: 'BRAZIL' },
];

const SEA_LABELS = [
  { c: [-75.2, 15.2], t: 'Caribbean Sea' },
  { c: [-94, 8], t: 'Pacific Ocean' },
];

// "Plate boundary" leader callout: label anchor + the boundary point it points
// to (both [lon, lat]).
const PLATE_CALLOUT = {
  label: [-101, 0.5],
  target: [-90.5, 4.2],
  lines: ['Tectonic plate', 'boundaries'],
};

// Emphasized quakes, drawn on top of the grey historical markers.
const BREAKING_QUAKE_ID = 'us6000tjl2'; // Aug. 10, 2026 — the news peg (CNN red).
const HIGHLIGHT_QUAKE_ID = 'us6000t7zc'; // June 24, 2026 (orange).

// Callouts for notable quakes, keyed by USGS event id. dx/dy are pixel offsets
// from the projected point; `lines` render stacked.
const QUAKE_CALLOUTS = {
  // M7.4, Aug. 10, 2026 -- San Jose del Palmar, Colombia (the news peg).
  us6000tjl2: {
    dx: 14,
    dy: 4,
    anchor: 'start',
    lines: [{ t: '7.4 magnitude', bold: true }, { t: 'August 10, 2026' }],
  },
  us6000t7zc: {
    dx: -96,
    dy: 4,
    anchor: 'start',
    lines: [{ t: '7.2 magnitude', bold: true }, { t: 'June 24, 2026' }],
  },
};


function normalizeQuakes(rows) {
  return rows
    .map(d => ({
      id: d[FIELDS.id],
      lon: +d[FIELDS.lon],
      lat: +d[FIELDS.lat],
      mag: +d[FIELDS.mag],
      time: d[FIELDS.time],
      place: d[FIELDS.place],
    }))
    .filter(d => Number.isFinite(d.lon) && Number.isFinite(d.lat) && d.mag >= MIN_MAGNITUDE);
}

export default function (props, container) {
  const { initialWidth, isMobile } = props;

  const W = initialWidth;
  const legendH = 32;
  const mapW = W;

  // Mobile shows a taller geographic slice (MOBILE_EXTENT) so the portrait
  // frame is filled with real map, not empty sea. Corner points are passed as
  // a MultiPoint (not a Polygon): polygon edges are treated as geodesics by
  // d3, which bulges the bounds and squashes the map.
  const extent = isMobile ? MOBILE_EXTENT : EXTENT;
  const extentFeature = {
    type: 'MultiPoint',
    coordinates: [
      [extent.minLon, extent.minLat],
      [extent.maxLon, extent.minLat],
      [extent.maxLon, extent.maxLat],
      [extent.minLon, extent.maxLat],
    ],
  };

  // Fit the region to the width; height falls out of the aspect (taller on
  // mobile because MOBILE_EXTENT spans more latitude).
  const proj = geoMercator().fitWidth(mapW, extentFeature);
  const path = geoPath(proj);
  const mapH = Math.ceil(geoPath(proj).bounds(extentFeature)[1][1]);
  const H = legendH + mapH;

  const project = ([lon, lat]) => proj([lon, lat]);

  const quakes = normalizeQuakes(quakesRaw);
  // Slightly smaller markers on mobile, where the map is more crowded.
  const quakeR = isMobile ? 5 : QUAKE_RADIUS;

  const svg = container
    .append('svg')
    .attr('width', W)
    .attr('height', H)
    .attr('viewBox', `0 0 ${W} ${H}`);

  // --- Map --------------------------------------------------------------
  // Clip the map to its own rect so nothing (e.g. Mexico's border along the top
  // edge) bleeds up into the reserved legend band above it.
  const mapClipId = 'map-clip';
  svg
    .append('defs')
    .append('clipPath')
    .attr('id', mapClipId)
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', mapW)
    .attr('height', mapH);
  const map = svg
    .append('g')
    .attr('transform', `translate(0, ${legendH})`)
    .attr('clip-path', `url(#${mapClipId})`);

  // Sea background.
  map
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', mapW)
    .attr('height', mapH)
    .attr('fill', COLORS.sea);

  // Coastal drop shadow: a blurred copy of the land, drawn between the sea and
  // the land fill. The land fill on top hides the interior, leaving only the
  // outward blur as a soft shadow the land casts onto the water.
  const shadowId = 'land-shadow';
  const shadowFilter = svg
    .append('defs')
    .append('filter')
    .attr('id', shadowId)
    .attr('x', '-5%')
    .attr('y', '-5%')
    .attr('width', '110%')
    .attr('height', '110%');
  shadowFilter
    .append('feGaussianBlur')
    .attr('in', 'SourceGraphic')
    .attr('stdDeviation', 3);
  map
    .append('path')
    .datum(borders.polys)
    .attr('d', path)
    .attr('fill', COLORS.coastShadow)
    .attr('filter', `url(#${shadowId})`);

  // Land fill (no stroke, per CNN README).
  map.append('path').datum(borders.polys).attr('d', path).attr('fill', COLORS.land);

  // Colombia (the news peg) filled a touch darker than its neighbors. Drawn
  // before the hillshade so terrain shading still reads over it.
  map.append('path').datum(colombia).attr('d', path).attr('fill', COLORS.landColombia);

  // Subtle shaded relief on land only. The grayscale image (flat land = white,
  // mountains = gray) is clipped to the land polygons and multiplied over the
  // white fill, so only terrain reads as soft shadow and the sea stays flat.
  const clipId = 'hillshade-land-clip';
  svg
    .append('defs')
    .append('clipPath')
    .attr('id', clipId)
    .append('path')
    .datum(borders.polys)
    .attr('d', path);
  map
    .append('image')
    .attr('href', isMobile ? hillshadeMobile : hillshadeDesktop)
    .attr('xlink:href', isMobile ? hillshadeMobile : hillshadeDesktop)
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', mapW)
    .attr('height', mapH)
    .attr('preserveAspectRatio', 'none')
    .attr('clip-path', `url(#${clipId})`)
    .attr('opacity', 0.15)
    .style('mix-blend-mode', 'multiply');

  // Country lines.
  map
    .append('path')
    .datum(borders.lines)
    .attr('d', path)
    .attr('fill', 'none')
    .attr('stroke', COLORS.countryLine)
    .attr('stroke-width', 0.8);

  // Disputed lines (dotted, if any).
  if (borders.disputed && borders.disputed.coordinates.length) {
    map
      .append('path')
      .datum(borders.disputed)
      .attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', COLORS.disputed)
      .attr('stroke-width', 0.8)
      .attr('stroke-linecap', 'round')
      .attr('stroke-dasharray', '0.1,2.6');
  }

  // Tectonic plate boundaries.
  map
    .append('path')
    .datum(plates)
    .attr('d', path)
    .attr('fill', 'none')
    .attr('stroke', COLORS.plate)
    .attr('stroke-width', 1.6)
    .attr('stroke-opacity', 0.35)
    .attr('stroke-linejoin', 'round')
    .attr('stroke-linecap', 'round');

  // Quake circles: uniform size, all grey. Emphasized quakes are skipped here
  // and drawn separately below (red breaking, orange highlight on desktop only).
  const quakePts = quakes.filter(
    d => d.id !== BREAKING_QUAKE_ID && (isMobile || d.id !== HIGHLIGHT_QUAKE_ID),
  );
  map
    .append('g')
    .selectAll('circle')
    .data(quakePts)
    .join('circle')
    .attr('cx', d => project([d.lon, d.lat])[0])
    .attr('cy', d => project([d.lon, d.lat])[1])
    .attr('r', quakeR)
    .attr('fill', QUAKE_GREY)
    .attr('fill-opacity', 0.25)
    .attr('stroke', QUAKE_GREY)
    .attr('stroke-width', 1.5);

  // Breaking-news quake: same marker treatment as the rest, just CNN red.
  const breaking = quakes.find(d => d.id === BREAKING_QUAKE_ID);
  if (breaking) {
    const hp = project([breaking.lon, breaking.lat]);
    map
      .append('circle')
      .attr('cx', hp[0])
      .attr('cy', hp[1])
      .attr('r', quakeR)
      .attr('fill', QUAKE_RED)
      .attr('fill-opacity', 0.25)
      .attr('stroke', QUAKE_RED)
      .attr('stroke-width', 2.25);
  }
  // Highlight quake: orange on desktop, grey with the rest on mobile.
  const highlight = quakes.find(d => d.id === HIGHLIGHT_QUAKE_ID);
  if (!isMobile && highlight) {
    const hp = project([highlight.lon, highlight.lat]);
    map
      .append('circle')
      .attr('cx', hp[0])
      .attr('cy', hp[1])
      .attr('r', quakeR)
      .attr('fill', QUAKE_ORANGE)
      .attr('fill-opacity', 0.25)
      .attr('stroke', QUAKE_ORANGE)
      .attr('stroke-width', 2.25);
  }


  // --- Labels -----------------------------------------------------------
  const labelG = map.append('g');

  labelG
    .selectAll('text.country-label')
    .data(COUNTRY_LABELS.filter(d => !(isMobile && d.hideMobile)))
    .join('text')
    .attr('class', 'country-label')
    .attr('x', d => project(d.c)[0])
    .attr('y', d => project(d.c)[1] + (isMobile && d.mobileDy ? d.mobileDy : 0))
    .attr('text-anchor', 'middle')
    .text(d => d.t);

  // On mobile the "Pacific Ocean" label is tucked directly under the tectonic
  // plate boundaries label (left-aligned to it) so it doesn't cross the line.
  const platePt = project(PLATE_CALLOUT.label);
  const plateBlockBottom = platePt[1] + (isMobile ? 12 : 0) + 14;
  const pacificMobile = d => isMobile && d.t === 'Pacific Ocean';
  labelG
    .selectAll('text.sea-label')
    .data(SEA_LABELS)
    .join('text')
    .attr('class', 'sea-label')
    .attr('x', d => (pacificMobile(d) ? platePt[0] : project(d.c)[0]))
    .attr('y', d => (pacificMobile(d) ? plateBlockBottom + 64 : project(d.c)[1]))
    .attr('text-anchor', d => (pacificMobile(d) ? 'start' : 'middle'))
    .text(d => d.t);

  // Plate boundary label (wrapped over two lines; nudged down on mobile so it
  // clears the boundary line at this tighter zoom).
  const pcLabel = project(PLATE_CALLOUT.label);
  const pcX = pcLabel[0];
  const pcY = pcLabel[1] + (isMobile ? 12 : 0);
  const plateText = map
    .append('text')
    .attr('class', 'plate-label')
    .attr('x', pcX)
    .attr('y', pcY)
    .attr('text-anchor', 'start');
  PLATE_CALLOUT.lines.forEach((line, i) => {
    plateText
      .append('tspan')
      .attr('x', pcX)
      .attr('dy', i === 0 ? 0 : 14)
      .text(line);
  });

  // --- Quake annotations (desktop only) ---------------------------------
  if (!isMobile) {
    const annG = map.append('g');
    quakes
      .filter(d => QUAKE_CALLOUTS[d.id])
      .forEach((d) => {
        const cfg = QUAKE_CALLOUTS[d.id];
        const p = project([d.lon, d.lat]);
        const x = p[0] + cfg.dx;
        const y = p[1] + cfg.dy;
        const t = annG
          .append('text')
          .attr('x', x)
          .attr('y', y)
          .attr('text-anchor', cfg.anchor || 'start');
        cfg.lines.forEach((line, i) => {
          t
            .append('tspan')
            .attr('class', line.bold ? 'annotation' : 'annotation-sub')
            .attr('x', x)
            .attr('dy', i === 0 ? 0 : 15)
            .text(line.t);
        });
      });
  }

  // --- Legend -----------------------------------------------------------
  // Drawn last so it always sits on top of the map, in the reserved band above
  // it (0..legendH). A single circle swatch (matching the map markers) next to
  // the descriptor. Size no longer encodes magnitude, so this is just the key.
  const legend = svg.append('g').attr('transform', 'translate(2, 0)');
  const cy = 15;
  const r = quakeR;
  legend
    .append('circle')
    .attr('cx', r)
    .attr('cy', cy)
    .attr('r', r)
    .attr('fill', QUAKE_GREY)
    .attr('fill-opacity', 0.25)
    .attr('stroke', QUAKE_GREY)
    .attr('stroke-width', 1.5);
  legend
    .append('text')
    .attr('class', 'legend-label')
    .attr('x', r * 2 + 6)
    .attr('y', cy)
    .attr('dominant-baseline', 'central')
    .text('Magnitude 7+ earthquakes since 1990');
}
